import { format, subDays } from 'date-fns';
import { useMemo, useState } from 'react';

import type { AuditLogFilters } from '@/entities/tenant';
import { Pager } from '@/shared/ui';

import { AdminHeader } from './components/AdminHeader';
import { AdminRegion } from './components/AdminRegion';
import { AuditLogCard } from './components/AuditLogCard';
import { MetricsRow } from './components/MetricsRow';
import { adminCopy } from './copy';
import { useAuditLogs, usePlatformMetrics, usePlatformTenants } from './hooks';

export interface AdminPageProps {
    /** `audit`: alleen de audit log, op zijn eigen adres. */
    view?: 'overview' | 'audit';
}

/**
 * Platformbeheer - overzicht.
 *
 * Vier getallen over alle organisaties heen (de nachtelijke momentopname van de
 * laatste dertig dagen), elk met een vervolgactie, en daaronder de audit log.
 * Dit scherm staat niet in het oorspronkelijke ontwerp; het volgt de vormtaal
 * van het bedrijfsdashboard (scherm 2).
 */
export function AdminPage({ view = 'overview' }: AdminPageProps) {
    const [filters, setFilters] = useState<AuditLogFilters>({ page: 1 });

    const period = useMemo(() => {
        const today = new Date();

        return {
            from: format(subDays(today, 30), 'yyyy-MM-dd'),
            until: format(today, 'yyyy-MM-dd'),
        };
    }, []);

    const auditOnly = view === 'audit';
    const metrics = usePlatformMetrics(period, !auditOnly);
    const audit = useAuditLogs(filters);
    const tenants = usePlatformTenants({});
    const tenantTotal = tenants.data?.meta?.total ?? tenants.data?.items.length ?? 0;

    return (
        <div className="flex min-h-0 flex-1 flex-col">
            <AdminHeader
                eyebrow={
                    auditOnly
                        ? `${adminCopy.eyebrowPlatform} · ${adminCopy.auditPageEyebrow}`
                        : `${adminCopy.eyebrowPlatform} · ${adminCopy.tenantCount(tenantTotal)} · ${adminCopy.overviewPeriod}`
                }
                title={auditOnly ? adminCopy.auditPageTitle : adminCopy.overviewTitle}
            />

            <div className="flex flex-1 flex-col gap-[14px] overflow-auto bg-[var(--surface-page)] px-7 pt-[22px] pb-8">
                {auditOnly ? null : (
                    <AdminRegion title={adminCopy.metricsTitle} query={metrics} skeletonHeight={92}>
                        {metrics.data ? <MetricsRow metrics={metrics.data} /> : null}
                    </AdminRegion>
                )}

                <AdminRegion title={adminCopy.auditTitle} query={audit}>
                    {audit.data ? (
                        <AuditLogCard
                            entries={audit.data.items}
                            filters={filters}
                            onFiltersChange={setFilters}
                            tenants={tenants.data?.items ?? []}
                            footer={
                                <Pager
                                    meta={audit.data.meta}
                                    noun={adminCopy.auditNoun}
                                    busy={audit.isFetching}
                                    onPage={(page) => setFilters({ ...filters, page })}
                                />
                            }
                        />
                    ) : null}
                </AdminRegion>
            </div>
        </div>
    );
}
